import type {
  InboxCallEvent,
  InboxConversationDetail,
  InboxHideState,
  InboxMessage,
  ReplyAttachmentRule,
} from '@sbc/domain-models/gateway';
import {
  COMMENT_DELETED_REFUSAL,
  FACEBOOK_GROUP_REPLIES_OFF,
  INTERCOM_NO_TEAMMATE,
  replyAttachmentRefusal,
  replyAttachmentRule,
  replyAttachmentTypes,
  replyMaxLength,
  WHATSAPP_WINDOW_CLOSED,
  channelHidesComments,
  channelRepliesFromInbox,
  commentDeletedByAuthor,
} from '@sbc/domain-models/gateway';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  EyeOff,
  FileText,
  ImageIcon,
  type LucideIcon,
  Mail,
  MessageSquareText,
  PanelRight,
  Paperclip,
  PhoneMissed,
  Sparkles,
  Voicemail,
  X,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ownershipConflictOf,
  useInboxMember,
  useInboxPostPreview,
  useSendInboxReply,
  useSetInboxMessageHidden,
} from '@/lib/api/inbox';
import { cn } from '@/lib/utils';

import { InboxAvatar } from './inbox-avatar';
import {
  CATEGORY_LABELS,
  CHANNEL_NAMES,
  SMS_KEYWORD_LABELS,
  STATUS_LABELS,
  SURFACE_LABELS,
  composerPlaceholder,
  contactPhone,
  deliveryNote,
  displayName,
  draftPrefillKey,
  groupHiddenMessages,
  memberLinked,
  metaInboxLabel,
  openOriginalLabel,
  partnershipNotice,
  prefilledComposerText,
  quoCannotTextNotice,
  replyAsHandle,
  replyLimitNotice,
  replyWindowClosed,
  senderLabel,
  closedRepeatsNote,
} from './inbox-format';
import { composerKeyIntent } from './inbox-keys';
import { firstNameOf, insertMacro, unfilledPlaceholders } from './inbox-macros';
import { MacroPicker } from './macro-picker';
import { MessageAttachment, MessageMedia } from './message-media';

export function ConversationThread({
  conversation,
  onBack,
  onMarkUnread,
  onOpenDetails,
}: {
  conversation: InboxConversationDetail;
  onBack: () => void;
  onMarkUnread: () => void;
  onOpenDetails: () => void;
}) {
  const name = displayName(conversation);
  const phone = contactPhone(conversation);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageId = conversation.messages.at(-1)?.id;

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, [lastMessageId]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b px-3 py-2.5">
        <Button
          variant="ghost"
          size="sm"
          className="size-8 shrink-0 px-0 md:hidden"
          aria-label="Back to conversations"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <InboxAvatar name={name} channel={conversation.channel} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{name}</p>
          <p className="truncate text-xs text-[var(--color-muted-foreground)]">
            {CHANNEL_NAMES[conversation.channel]} · {SURFACE_LABELS[conversation.surface]} ·{' '}
            {phone ? `${phone} · ` : ''}
            {conversation.handle}
          </p>
        </div>
        <div className="hidden items-center gap-1.5 sm:flex">
          {conversation.category && (
            <Badge variant={conversation.category === 'order_issue' ? 'destructive' : 'secondary'}>
              {CATEGORY_LABELS[conversation.category]}
            </Badge>
          )}
          <Badge variant={conversation.status === 'needs_reply' ? 'default' : 'outline'}>
            {STATUS_LABELS[conversation.status]}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 px-2"
          onClick={onMarkUnread}
          aria-label="Mark as unread"
          title="Mark as unread (U)"
        >
          <Mail className="size-4" />
          <span className="hidden lg:inline">Mark as unread</span>
        </Button>
        {conversation.metaInboxLink && (
          <Button variant="ghost" size="sm" className="shrink-0 px-2" asChild>
            <a
              href={conversation.metaInboxLink.url}
              target="_blank"
              rel="noreferrer"
              aria-label={metaInboxLabel(conversation.metaInboxLink)}
              title={metaInboxLabel(conversation.metaInboxLink)}
            >
              <ExternalLink className="size-4" />
              <span className="hidden lg:inline">Business Suite</span>
            </a>
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 xl:hidden"
          onClick={onOpenDetails}
          aria-label="Show details"
        >
          <PanelRight className="size-4" />
          <span className="hidden sm:inline">Details</span>
        </Button>
      </header>

      <div
        ref={scrollRef}
        className="relative min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-4 sm:px-6"
      >
        {conversation.surface === 'comment_thread' && <PostCard conversation={conversation} />}
        <ClosedRepeatsNote conversation={conversation} />
        {conversation.subject && (
          <p className="text-center text-xs text-[var(--color-muted-foreground)]">
            Subject: <span className="font-medium">{conversation.subject}</span>
          </p>
        )}
        {conversation.messages.length === 0 && (
          <p className="text-center text-sm text-[var(--color-muted-foreground)]">No messages.</p>
        )}
        {groupHiddenMessages(conversation.messages).map((item) =>
          item.kind === 'message' ? (
            <MessageBubble
              key={item.message.id}
              message={item.message}
              conversation={conversation}
            />
          ) : (
            <HiddenGroup
              key={item.messages[0]?.id}
              messages={item.messages}
              conversation={conversation}
            />
          ),
        )}
      </div>

      <ReplyComposer key={conversation.id} conversation={conversation} />
    </div>
  );
}

function ClosedRepeatsNote({ conversation }: { conversation: InboxConversationDetail }) {
  const note = closedRepeatsNote(conversation);
  if (!note) {
    return null;
  }
  return (
    <p className="mx-auto max-w-xl rounded-lg border border-dashed px-3 py-2 text-center text-xs text-[var(--color-muted-foreground)]">
      {note}
    </p>
  );
}

function PostCard({ conversation }: { conversation: InboxConversationDetail }) {
  const preview = useInboxPostPreview(conversation.id, conversation.externalMediaRef !== null);
  const [broken, setBroken] = useState(false);
  const thumbnail = broken ? null : (preview.data?.thumbnailUrl ?? null);
  return (
    <div className="mx-auto flex max-w-xl items-start gap-3 rounded-lg border bg-[var(--color-card)] p-3">
      <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-[var(--color-muted)]">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt="Post"
            className="size-full object-cover"
            loading="lazy"
            onError={() => setBroken(true)}
          />
        ) : (
          <ImageIcon className="size-6 text-[var(--color-muted-foreground)]" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-[var(--color-muted-foreground)]">
          {conversation.postOwnerHandle
            ? `Comments on @${conversation.postOwnerHandle}'s partnership ad`
            : 'Comments on a post'}
        </p>
        <p className="mt-0.5 line-clamp-3 text-sm whitespace-pre-wrap">
          {conversation.postCaption ?? 'No caption.'}
        </p>
        {conversation.permalink && (
          <a
            href={conversation.permalink}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block text-xs underline"
          >
            View post
          </a>
        )}
      </div>
    </div>
  );
}

const HIDE_STATE_LABELS: Record<InboxHideState, string> = {
  pending: 'Hiding…',
  hidden: 'Hidden',
  failed: 'Hide failed',
};

const BOT_TAKE_DOWN_LABELS: Record<InboxHideState, string> = {
  pending: 'Bot taking down…',
  hidden: 'Taken down by the bot',
  failed: 'Bot could not take it down',
};

function HiddenGroup({
  messages,
  conversation,
}: {
  messages: InboxMessage[];
  conversation: InboxConversationDetail;
}) {
  const [open, setOpen] = useState(false);
  const verdicts = [
    ...new Set(messages.map((message) => message.moderationVerdict).filter(Boolean)),
  ];
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="space-y-2">
      <button
        type="button"
        className="mx-auto flex items-center gap-1.5 rounded-full border border-dashed px-3 py-1 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-accent)]"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <EyeOff className="size-3.5" />
        {messages.length} hidden {messages.length === 1 ? 'comment' : 'comments'}
        {verdicts.length > 0 && ` (${verdicts.join(', ')})`}
        <Chevron className="size-3.5" />
      </button>
      {open &&
        messages.map((message) => (
          <MessageBubble key={message.id} message={message} conversation={conversation} />
        ))}
    </div>
  );
}

function MessageBubble({
  message,
  conversation,
}: {
  message: InboxMessage;
  conversation: InboxConversationDetail;
}) {
  const outbound = message.direction === 'outbound';
  const failed = message.deliveryStatus === 'failed';
  const deleted = message.deletedAt !== null;
  const hidden = message.hideState !== null;
  const comment = conversation.surface === 'comment_thread';
  const setHidden = useSetInboxMessageHidden(conversation.id);
  const canModerate =
    comment && !outbound && !deleted && channelHidesComments(conversation.channel);
  const flaggedOnly =
    conversation.channel === 'facebook_group' &&
    !outbound &&
    message.hideState === null &&
    message.moderationVerdict !== null &&
    message.moderationVerdict !== 'keep';
  const sender = senderLabel(message, conversation.channel);
  const delivery = deliveryNote(message, conversation.channel);
  const time = new Date(message.sentAt).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className={cn('flex flex-col', outbound ? 'items-end' : 'items-start')}>
      {comment && !outbound && message.authorUsername && (
        <span className="mb-0.5 px-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          {message.authorUsername}
        </span>
      )}
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3.5 py-2 text-sm sm:max-w-[75%]',
          outbound
            ? 'rounded-br-sm bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
            : 'rounded-bl-sm bg-[var(--color-muted)]',
          failed && 'ring-2 ring-[var(--color-destructive)]',
          (hidden || deleted) && 'opacity-60',
        )}
      >
        {message.callEvent && <CallEventLine event={message.callEvent} />}
        {message.mediaType && (
          <MessageMedia
            message={message}
            conversationId={conversation.id}
            channel={conversation.channel}
          />
        )}
        {message.text && (
          <p className={cn('break-words whitespace-pre-wrap', deleted && 'line-through')}>
            {message.text}
          </p>
        )}
        {message.attachments.length > 0 && (
          <div className="mt-1 space-y-1">
            {message.attachments.map((attachment) => (
              <MessageAttachment
                key={attachment.id}
                attachment={attachment}
                conversationId={conversation.id}
                messageId={message.id}
              />
            ))}
          </div>
        )}
        {message.quotedText && <QuotedText text={message.quotedText} />}
      </div>
      <span className="mt-0.5 flex flex-wrap items-center gap-x-2 px-1 text-[11px] text-[var(--color-muted-foreground)]">
        <span>{time}</span>
        {sender && <span>{sender}</span>}
        {message.smsKeyword && <span>{SMS_KEYWORD_LABELS[message.smsKeyword]}</span>}
        {delivery && <span>{delivery}</span>}
        {failed && <span className="text-[var(--color-destructive)]">Not delivered</span>}
        {deleted && <span>{message.deletedVia === 'unsend' ? 'Unsent' : 'Deleted'}</span>}
        {message.hideState && (
          <span>
            {conversation.channel === 'facebook_group'
              ? BOT_TAKE_DOWN_LABELS[message.hideState]
              : HIDE_STATE_LABELS[message.hideState]}
            {message.moderationVerdict && message.moderationVerdict !== 'keep'
              ? `: ${message.moderationVerdict}`
              : ''}
          </span>
        )}
        {flaggedOnly && <span>Flagged: {message.moderationVerdict}</span>}
        {canModerate && (
          <button
            type="button"
            className="underline-offset-2 hover:underline disabled:opacity-50"
            disabled={setHidden.isPending}
            onClick={() => setHidden.mutate({ messageId: message.id, hidden: !hidden })}
          >
            {hidden ? 'Unhide' : 'Hide'}
          </button>
        )}
      </span>
      {failed && message.failureReason && (
        <span className="px-1 text-[11px] text-[var(--color-destructive)]">
          {message.failureReason}
        </span>
      )}
      {setHidden.isError && (
        <span className="px-1 text-[11px] text-[var(--color-destructive)]">
          {setHidden.error.message}
        </span>
      )}
    </div>
  );
}

function QuotedText({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button
        type="button"
        aria-expanded={open}
        className="text-[11px] underline-offset-2 opacity-80 hover:underline"
        onClick={() => setOpen((shown) => !shown)}
      >
        {open ? 'Hide quoted text' : 'Show quoted text'}
      </button>
      {open && (
        <p className="mt-1 border-l-2 border-current/30 pl-2 text-xs break-words whitespace-pre-wrap opacity-80">
          {text}
        </p>
      )}
    </div>
  );
}

function ReplyComposer({ conversation }: { conversation: InboxConversationDetail }) {
  const sendReply = useSendInboxReply(conversation.id);
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentRule = replyAttachmentRule(conversation.channel, conversation.surface);
  const [takeOverFrom, setTakeOverFrom] = useState<string | null>(null);
  const [macrosOpen, setMacrosOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCursor = useRef<number | null>(null);
  const prefilled = useRef<{ key: string; text: string } | null>(null);
  const aiDraft = conversation.draft;
  const maxLength = replyMaxLength(conversation.channel);
  const tooLong = text.trim().length > maxLength;
  const limitNotice = replyLimitNotice(conversation.channel, text.trim().length);
  const placeholders = unfilledPlaceholders(text);
  const missingText = !text.trim() && replyNeedsText(attachmentRule, file);
  const linked = memberLinked(conversation);
  const member = useInboxMember(conversation.id, linked && macrosOpen);
  const firstName = linked ? firstNameOf(member.data?.member?.name) : null;

  useLayoutEffect(() => {
    const cursor = pendingCursor.current;
    const element = textareaRef.current;
    if (cursor === null || !element) {
      return;
    }
    pendingCursor.current = null;
    element.focus();
    element.setSelectionRange(cursor, cursor);
  }, [text]);

  const insertMacroText = (macroText: string) => {
    const element = textareaRef.current;
    const next = insertMacro(
      text,
      macroText,
      element?.selectionStart ?? text.length,
      element?.selectionEnd ?? text.length,
    );
    pendingCursor.current = next.cursor;
    setText(next.text);
    setMacrosOpen(false);
  };

  useEffect(() => {
    if (!aiDraft || prefilled.current?.key === draftPrefillKey(aiDraft)) {
      return;
    }
    const previous = prefilled.current?.text ?? null;
    prefilled.current = { key: draftPrefillKey(aiDraft), text: aiDraft.text };
    setText((current) => prefilledComposerText(current, previous, aiDraft.text));
  }, [aiDraft]);

  const send = (confirmed = false) => {
    const reply = text.trim();
    if (missingText || tooLong || placeholders.length > 0 || sendReply.isPending) {
      return;
    }
    const owner = conversation.assignee;
    if (!confirmed && owner && !owner.isViewer) {
      setTakeOverFrom(owner.email);
      return;
    }
    setTakeOverFrom(null);
    sendReply.mutate(
      { text: reply, takeOver: confirmed || undefined, ...(file ? { file } : {}) },
      {
        onSuccess: () => {
          setText('');
          setFile(null);
        },
        onError: (error) => {
          const conflict = ownershipConflictOf(error);
          if (conflict) {
            setTakeOverFrom(conflict.owner.email);
          }
        },
      },
    );
  };

  if (!channelRepliesFromInbox(conversation.channel)) {
    return (
      <div className="border-t bg-[var(--color-background)] p-3">
        <NoticeBanner
          text={FACEBOOK_GROUP_REPLIES_OFF}
          permalink={conversation.permalink}
          linkLabel={openOriginalLabel(conversation.channel, conversation.surface)}
        />
      </div>
    );
  }

  if (conversation.channel === 'intercom' && !conversation.intercomSender) {
    return (
      <div className="border-t bg-[var(--color-background)] p-3">
        <NoticeBanner
          text={INTERCOM_NO_TEAMMATE}
          permalink={conversation.permalink}
          linkLabel={openOriginalLabel(conversation.channel, conversation.surface)}
        />
      </div>
    );
  }

  const untextable = quoCannotTextNotice(conversation);
  if (untextable) {
    return (
      <div className="border-t bg-[var(--color-background)] p-3">
        <NoticeBanner text={untextable} permalink={null} />
      </div>
    );
  }

  const partnership = partnershipNotice(conversation);
  if (partnership?.blocked) {
    return (
      <div className="border-t bg-[var(--color-background)] p-3">
        <NoticeBanner text={partnership.text} permalink={conversation.permalink} />
      </div>
    );
  }
  if (replyWindowClosed(conversation)) {
    return (
      <div className="border-t bg-[var(--color-background)] p-3">
        <NoticeBanner text={WHATSAPP_WINDOW_CLOSED} permalink={null} />
      </div>
    );
  }
  if (commentDeletedByAuthor(conversation)) {
    return (
      <div className="border-t bg-[var(--color-background)] p-3">
        <NoticeBanner text={COMMENT_DELETED_REFUSAL} permalink={null} />
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t bg-[var(--color-background)] p-3">
      {partnership && <NoticeBanner text={partnership.text} permalink={conversation.permalink} />}
      {aiDraft?.actionRequired && (
        <div className="flex items-start gap-2 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 p-2.5 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--color-destructive)]" />
          <p>
            <span className="font-semibold">Do first: </span>
            {aiDraft.actionRequired}
          </p>
        </div>
      )}
      <DraftNotice conversation={conversation} />
      {macrosOpen && (
        <MacroPicker
          channel={conversation.channel}
          firstName={firstName}
          onPick={insertMacroText}
          onClose={() => {
            setMacrosOpen(false);
            textareaRef.current?.focus();
          }}
        />
      )}
      <Textarea
        ref={textareaRef}
        rows={3}
        maxLength={maxLength}
        aria-label="Reply"
        className="max-h-48 min-h-16 resize-none"
        placeholder={composerPlaceholder(conversation)}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          const intent = composerKeyIntent({
            key: event.key,
            shiftKey: event.shiftKey,
            isComposing: event.nativeEvent.isComposing,
          });
          if (intent === 'send') {
            event.preventDefault();
            send();
          }
        }}
      />
      {conversation.channel === 'tiktok' && (
        <p
          className={cn(
            'text-xs',
            tooLong ? 'text-[var(--color-destructive)]' : 'text-[var(--color-muted-foreground)]',
          )}
        >
          TikTok replies hold at most {maxLength} characters, emoji and spaces included ·{' '}
          {maxLength - text.trim().length} left
        </p>
      )}
      {limitNotice && conversation.channel !== 'tiktok' && (
        <p className="text-xs text-[var(--color-destructive)]">{limitNotice}</p>
      )}
      {placeholders.length > 0 && (
        <p className="text-xs text-[var(--color-destructive)]">
          Fill {placeholders.join(', ')} before you send.
        </p>
      )}
      {takeOverFrom && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border p-2.5 text-sm">
          <p>{takeOverFrom} is handling this. Take over and send?</p>
          <Button size="sm" disabled={sendReply.isPending} onClick={() => send(true)}>
            Take over and send
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setTakeOverFrom(null)}>
            Cancel
          </Button>
        </div>
      )}
      {sendReply.isError && !takeOverFrom && (
        <p className="text-sm text-[var(--color-destructive)]">{sendReply.error.message}</p>
      )}
      {file && (
        <div className="flex w-fit max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
          {file.type === 'application/pdf' ? (
            <FileText className="size-3.5 shrink-0" />
          ) : (
            <ImageIcon className="size-3.5 shrink-0" />
          )}
          <span className="truncate">{file.name}</span>
          <span className="shrink-0 text-[var(--color-muted-foreground)]">
            {fileSizeLabel(file.size)}
          </span>
          <button
            type="button"
            aria-label="Remove file"
            className="shrink-0 opacity-70 hover:opacity-100"
            onClick={() => setFile(null)}
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}
      {fileError && (
        <p id={FILE_NOTICE_ID} className="text-xs text-[var(--color-destructive)]">
          {fileError}
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          aria-expanded={macrosOpen}
          onClick={() => setMacrosOpen((open) => !open)}
        >
          <MessageSquareText className="size-4" />
          Macros
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={replyAttachmentTypes(attachmentRule).join(',')}
          className="hidden"
          aria-label="File to attach"
          onChange={(event) => {
            const picked = event.target.files?.[0] ?? null;
            event.target.value = '';
            const refusal = picked ? replyAttachmentRefusal(attachmentRule, picked) : null;
            setFileError(refusal);
            setFile(refusal ? null : picked);
          }}
        />
        <Button
          variant="outline"
          size="sm"
          className={cn('shrink-0', !attachmentRule.accepts && 'opacity-50')}
          aria-label="Attach a file"
          aria-disabled={!attachmentRule.accepts}
          aria-describedby={fileError ? FILE_NOTICE_ID : undefined}
          title={attachmentRule.accepts ? undefined : attachmentRule.reason}
          onClick={() =>
            attachmentRule.accepts
              ? fileInputRef.current?.click()
              : setFileError(attachmentRule.reason)
          }
        >
          <Paperclip className="size-4" />
          <span className="hidden sm:inline">Attach</span>
        </Button>
        <p className="hidden min-w-0 truncate text-xs text-[var(--color-muted-foreground)] sm:block">
          Enter to send · Shift+Enter for a new line
        </p>
        <Button
          size="sm"
          className="ml-auto"
          onClick={() => send()}
          disabled={sendReply.isPending || missingText || tooLong || placeholders.length > 0}
        >
          {sendReply.isPending ? 'Sending…' : `Reply as ${replyAsHandle(conversation)}`}
        </Button>
      </div>
    </div>
  );
}

const FILE_NOTICE_ID = 'reply-file-notice';

export function replyNeedsText(rule: ReplyAttachmentRule, file: File | null): boolean {
  return file === null || (rule.accepts && rule.textRequired);
}

export function fileSizeLabel(bytes: number): string {
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.max(1, Math.round(kb))} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

const CALL_EVENT_LINES: Record<InboxCallEvent, { icon: LucideIcon; label: string }> = {
  missed_call: { icon: PhoneMissed, label: 'Missed call' },
  voicemail: { icon: Voicemail, label: 'Voicemail' },
};

function CallEventLine({ event }: { event: InboxCallEvent }) {
  const { icon: Icon, label } = CALL_EVENT_LINES[event];
  return (
    <p className="flex items-center gap-1.5 font-medium">
      <Icon className="size-3.5 shrink-0" aria-hidden="true" />
      {label}
    </p>
  );
}

function NoticeBanner({
  text,
  permalink,
  linkLabel = 'Open the post',
}: {
  text: string;
  permalink: string | null;
  linkLabel?: string;
}) {
  return (
    <div className="flex items-start gap-2 rounded-md border p-2.5 text-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--color-muted-foreground)]" />
      <p>
        {text}
        {permalink && (
          <>
            {' '}
            <a href={permalink} target="_blank" rel="noreferrer" className="underline">
              {linkLabel}
            </a>
          </>
        )}
      </p>
    </div>
  );
}

function DraftNotice({ conversation }: { conversation: InboxConversationDetail }) {
  if (conversation.draftStatus === 'ready' && conversation.draft) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]">
        <Sparkles className="size-3.5" />
        AI draft ({conversation.draft.confidence} confidence). Read and edit it before you send.
      </p>
    );
  }
  if (conversation.draftStatus === 'pending') {
    return (
      <p className="text-xs text-[var(--color-muted-foreground)]">Drafting a suggested reply…</p>
    );
  }
  if (conversation.draftStatus === 'withheld') {
    return (
      <p className="text-xs text-[var(--color-muted-foreground)]">
        {conversation.draftWithheldReason ?? 'The AI draft was withheld.'}
      </p>
    );
  }
  return null;
}
